import React, { useState, useRef, useEffect, useCallback } from 'react';
import './QuestInfoEditor.css';
import FieldImageGallery from './FieldImageGallery';
import ImageModal from './ImageModal';
import { useCharacterCounter } from '../hooks/useCharacterCounter';
import CharacterCounter from './CharacterCounter';
import { placeCaret } from '../utils/caret';

const formatBytes = (bytes, decimals = 2) => {
    if (!bytes || bytes === 0) return '0 Bytes';
    const k = 1024;
    const dm = decimals < 0 ? 0 : decimals;
    const sizes = ['Bytes', 'KB', 'MB', 'GB', 'TB'];
    const i = Math.floor(Math.log(bytes) / Math.log(k));
    return parseFloat((bytes / Math.pow(k, i)).toFixed(dm)) + ' ' + sizes[i];
};

const QuestInfoEditor = ({ questInfo, questId, selectedTable, locations, onQuestInfoUpdate, isReadOnly }) => {
  const [editingField, setEditingField] = useState(null);
  const [questImages, setQuestImages] = useState([]);
  const [questAudioFiles, setQuestAudioFiles] = useState([]);
  const [questOriginalAudioFiles, setQuestOriginalAudioFiles] = useState([]); // WAV originals
  const [questVideos, setQuestVideos] = useState([]);
  const [modalImage, setModalImage] = useState(null);
  const editableRef = useRef(null);
  const clickPointRef = useRef(null);

  // Initialize quest_info if it doesn't exist
  const currentQuestInfo = questInfo || {
    quest_name: '',
    final_message: '',
    quest_description: ''
  };

  // Fetch the quest's image list (reusable so gallery edits can refresh it)
  const refreshQuestImages = useCallback(async () => {
    if (!questId) return;
    try {
      const imgResponse = await fetch(`/api/quest-images/${questId}?table=${selectedTable}`, {
        credentials: 'include'
      });

      if (!imgResponse.ok) {
        throw new Error('Failed to fetch quest images');
      }

      const imgData = await imgResponse.json();
      setQuestImages(imgData);
    } catch (error) {
      console.error('Error fetching quest images:', error);
    }
  }, [questId, selectedTable]);

  // Fetch all media for the quest info
  useEffect(() => {
    if (!questId) return;

    const fetchQuestMedia = async () => {
      try {
        await refreshQuestImages();

        const audioResponse = await fetch(`/api/quest-audio/${questId}?table=${selectedTable}`, {
            credentials: 'include'
        });
        if (!audioResponse.ok) {
            throw new Error('Failed to fetch quest audio files');
        }
        const audioData = await audioResponse.json();
        setQuestAudioFiles(audioData);

        // Fetch original WAV audio files for counts
        const wavResponse = await fetch(`/api/quest-original-audio/${questId}?table=${selectedTable}`, {
            credentials: 'include'
        });
        if (!wavResponse.ok) {
            throw new Error('Failed to fetch quest original audio files');
        }
        const wavData = await wavResponse.json();
        setQuestOriginalAudioFiles(wavData);

        // Fetch video files (size_bytes, duration_sec per file)
        const videoResponse = await fetch(`/api/quest-videos/${questId}?table=${selectedTable}`, {
            credentials: 'include'
        });
        if (!videoResponse.ok) {
            throw new Error('Failed to fetch quest videos');
        }
        const videoData = await videoResponse.json();
        setQuestVideos(videoData);

      } catch (error) {
        console.error('Error fetching quest media:', error);
      }
    };
    
    fetchQuestMedia();
  }, [questId, selectedTable, refreshQuestImages]);

  const totalLocations = locations ? locations.length : 0;
  const totalImageSize = questImages.reduce((acc, img) => acc + (Number(img.size) || 0), 0);
  const totalAudioSize = questAudioFiles.reduce((acc, audio) => acc + (Number(audio.size) || 0), 0);
  // Use precise durations returned from API (seconds)
  const totalDurationSeconds = questAudioFiles.reduce((acc, audio) => acc + (Number(audio.duration) || 0), 0);

  const totalVideoSize = questVideos.reduce((acc, v) => acc + (Number(v.size_bytes) || 0), 0);
  const totalVideoDurationSeconds = questVideos.reduce((acc, v) => acc + (Number(v.duration_sec) || 0), 0);
  const totalVideoFiles = questVideos.length;
  // What the player actually downloads: images + ogg audio + video (wav originals stay in the editor)
  const totalMediaSize = totalImageSize + totalAudioSize + totalVideoSize;

  const formatDuration = (seconds) => {
    if (!seconds) return '0m';
    const hours = Math.floor(seconds / 3600);
    const minutes = Math.floor((seconds % 3600) / 60);
    if (hours === 0) return `${minutes}m`;
    return `${hours}h ${minutes}m`;
  };

  const oggCount = questAudioFiles.length - questOriginalAudioFiles.length;  // because each wav is additionally converted to ogg
  const wavCount = questOriginalAudioFiles.length;
  const totalAudioFiles = oggCount + wavCount;

  const handleEditStart = (field, point) => {
    if (isReadOnly) return;
    setEditingField(field);
    clickPointRef.current = point || null;

    // Place the caret after the component has re-rendered as editable. Drop it
    // where the user clicked (not at the end) and don't scroll - see placeCaret.
    setTimeout(() => {
      placeCaret(editableRef.current, clickPointRef.current);
      clickPointRef.current = null;
    }, 0);
  };

  const handleEditBlur = (field) => {
    if (editableRef.current) {
      // Get the content, normalize empty content to empty string
      const content = editableRef.current.innerText.trim();
      const value = content === ' ' ? '' : content; // Handle non-breaking space
      
      // Update the quest info
      onQuestInfoUpdate(field, value);
    }
    setEditingField(null);
  };

  const handleKeyDown = (e, field) => {
    if (e.key === 'Enter' && !e.shiftKey) {
      e.preventDefault();
      
      if (editableRef.current) {
        // Get the content, normalize empty content to empty string
        const content = editableRef.current.innerText.trim();
        const value = content === ' ' ? '' : content; // Handle non-breaking space
        
        // Update the quest info
        onQuestInfoUpdate(field, value);
      }
      
      setEditingField(null);
    }
  };

  // Helper function to escape HTML
  const escapeHtml = (unsafe) => {
    if (typeof unsafe !== 'string') return '';
    return unsafe
      .replace(/&/g, '&amp;')
      .replace(/</g, '&lt;')
      .replace(/>/g, '&gt;')
      .replace(/"/g, '&quot;')
      .replace(/'/g, '&#039;');
  }

  // Ordered gallery for a quest_info field: [{ id, url, size }]
  const getImagesFor = (fieldType) => {
    // Server returns the list already sorted by gallery order
    return questImages
      .filter(img => img.location_id === 'quest_info' && img.field_type === fieldType)
      .map(img => ({
        id: img.id,
        size: img.size,
        caption: img.caption,
        url: `/api/image-file/${img.id}`
      }));
  };

  // Uploads append a new row on the server; just refetch the gallery data
  const handleImageUploaded = () => {
    refreshQuestImages();
  };

  const handleDeleteImage = async (image) => {
    if (isReadOnly || !image?.id) return;
    if (!window.confirm('Are you sure you want to delete this image?')) return;
    try {
      const response = await fetch(`/api/image-file/${image.id}`, {
        method: 'DELETE',
        credentials: 'include'
      });
      if (!response.ok) {
        const errorData = await response.json();
        throw new Error(errorData.error || 'Failed to delete image');
      }
      setQuestImages(prev => prev.filter(img => img.id !== image.id));
    } catch (error) {
      console.error('Image deletion error:', error);
      alert(`Failed to delete image: ${error.message}`);
    }
  };

  // Set or clear an image caption (players see it under the photo in the app)
  const handleEditImageCaption = async (image) => {
    if (isReadOnly || !image?.id) return;
    const next = window.prompt('Image caption (leave empty to remove):', image.caption || '');
    if (next === null) return; // cancelled
    try {
      const response = await fetch(`/api/image-file/${image.id}`, {
        method: 'PATCH',
        headers: { 'Content-Type': 'application/json' },
        credentials: 'include',
        body: JSON.stringify({ caption: next })
      });
      if (!response.ok) {
        const errorData = await response.json();
        throw new Error(errorData.error || 'Failed to update caption');
      }
      const data = await response.json();
      setQuestImages(prev =>
        prev.map(img => (img.id === image.id ? { ...img, caption: data.caption } : img))
      );
    } catch (error) {
      console.error('Image caption error:', error);
      alert(`Failed to update caption: ${error.message}`);
    }
  };

  // Persist a new gallery order; re-sync from the server on failure
  const handleReorderImages = async (fieldType, orderedIds) => {
    if (isReadOnly) return;
    const questUuid = currentQuestInfo?.uuid;
    if (!questUuid) return;

    setQuestImages(prev => {
      const rest = prev.filter(img =>
        !(img.location_id === 'quest_info' && img.field_type === fieldType)
      );
      const pair = prev.filter(img =>
        img.location_id === 'quest_info' && img.field_type === fieldType
      );
      const byId = new Map(pair.map(img => [img.id, img]));
      const reordered = orderedIds.map(id => byId.get(id)).filter(Boolean);
      return [...rest, ...reordered];
    });

    try {
      const response = await fetch('/api/images/reorder', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        credentials: 'include',
        body: JSON.stringify({ locationUuid: questUuid, fieldType, orderedIds })
      });
      if (!response.ok) {
        const errorData = await response.json();
        throw new Error(errorData.error || 'Failed to reorder images');
      }
    } catch (error) {
      console.error('Image reorder error:', error);
      refreshQuestImages();
    }
  };

  // Open image modal when a thumbnail is clicked
  const handleThumbnailClick = (images, index) => {
    setModalImage({ images, index });
  };

  // Close the image modal
  const handleCloseModal = () => {
    setModalImage(null);
  };

  const EditableField = ({ field, value, multiline = false, maxLength }) => {
    const isEditing = editingField === field;
    const fieldId = `quest-info-${field}`;
    const fieldImages = getImagesFor(field);
    const charCount = useCharacterCounter(value, isEditing, editableRef);
    
    // Handle empty strings by replacing with a non-breaking space for display
    let displayValue = value === '' ? ' ' : value;
    
    // Always escape HTML to show tags as text
    displayValue = escapeHtml(displayValue);
    
    return (
      <div className="editable-field-container">
        <div 
          id={fieldId}
          className={`editable-field ${multiline ? 'multiline' : ''} ${isEditing ? 'editing' : ''}`}
          contentEditable={isEditing}
          suppressContentEditableWarning={true}
          ref={isEditing ? editableRef : null}
          onBlur={() => isEditing && handleEditBlur(field)}
          onKeyDown={(e) => isEditing && handleKeyDown(e, field)}
          onClick={(e) => !isEditing && handleEditStart(field, { x: e.clientX, y: e.clientY })}
          dangerouslySetInnerHTML={{ __html: displayValue }}
        />
        
        <CharacterCounter count={charCount} maxLength={maxLength} />

        <div className="field-actions">
          <FieldImageGallery
            images={fieldImages}
            locationId="quest_info"
            fieldType={field}
            questId={questId}
            onImageUploaded={handleImageUploaded}
            onDeleteImage={handleDeleteImage}
            onEditCaption={handleEditImageCaption}
            onReorder={(orderedIds) => handleReorderImages(field, orderedIds)}
            onThumbnailClick={(index) => handleThumbnailClick(fieldImages, index)}
            disabled={isReadOnly}
          />
        </div>
      </div>
    );
  };

  return (
    <div className="quest-info-editor">
      {modalImage && (
        <ImageModal
          images={modalImage.images}
          startIndex={modalImage.index}
          onClose={handleCloseModal}
        />
      )}
      
      <div className="quest-info-header">
        <h2>Quest Information</h2>
      </div>
      
      <div className="quest-meta-summary">
        <div className="meta-item">
            <span className="meta-label">Total Locations:</span>
            <span className="meta-value">{totalLocations}</span>
        </div>
        <div className="meta-item">
            <span className="meta-label">Images Size:</span>
            <span className="meta-value">{formatBytes(totalImageSize)}</span>
        </div>
        <div className="meta-item">
            <span className="meta-label">Audio Size:</span>
            <span className="meta-value">
              {formatBytes(totalAudioSize)}{totalAudioSize > 0 ? ` (${formatDuration(totalDurationSeconds)})` : ''}
            </span>
        </div>
        <div className="meta-item">
            <span className="meta-label">Audio Files:</span>
            <span className="meta-value">{totalAudioFiles} total / {oggCount} ogg / {wavCount} wav</span>
        </div>
        <div className="meta-item">
            <span className="meta-label">Video Size:</span>
            <span className="meta-value">
              {formatBytes(totalVideoSize)}{totalVideoSize > 0 ? ` (${formatDuration(totalVideoDurationSeconds)})` : ''}
            </span>
        </div>
        <div className="meta-item">
            <span className="meta-label">Video Files:</span>
            <span className="meta-value">{totalVideoFiles}</span>
        </div>
        <div className="meta-item">
            <span className="meta-label">Total Media:</span>
            <span className="meta-value">{formatBytes(totalMediaSize)}</span>
        </div>
      </div>

      <div className="quest-info-section">
        <h4>Quest Name</h4>
        <EditableField 
          field="quest_name" 
          value={currentQuestInfo.quest_name || ''} 
        />
      </div>
      
      <div className="quest-info-section">
        <h4>Quest Description</h4>
        <EditableField 
          field="quest_description" 
          value={currentQuestInfo.quest_description || ''} 
          multiline={true} 
          maxLength={4096}
        />
      </div>
      
      <div className="quest-info-section">
        <h4>Final Message</h4>
        <EditableField
          field="final_message"
          value={currentQuestInfo.final_message || ''}
          multiline={true}
          maxLength={4096}
        />
      </div>

      {/* quest_info.voiceover_hidden: lets a quest go to prod before its
          voice-over is checked. The app API then hides the takes from players
          (no badge, no player, no audio in the offline pack) while they stay
          in the audio tables. The flag travels with Sync to prod like any
          other quest_info field; untick and sync again to reveal the audio. */}
      <div className="quest-info-section">
        <h4>Voice-over in the app 🎧</h4>
        <label className="quest-info-checkbox">
          <input
            type="checkbox"
            checked={currentQuestInfo.voiceover_hidden === true}
            disabled={isReadOnly}
            onChange={(e) => onQuestInfoUpdate('voiceover_hidden', e.target.checked)}
          />
          <span>
            Hide voice-over from players
            {currentQuestInfo.voiceover_hidden === true
              ? ' — the app shows this quest without audio until unticked and synced to prod'
              : ''}
          </span>
        </label>
      </div>

      {/* Editorial guidance (quest_info.author_guidance): the quest-wide steer
          the pipeline feeds into the writer's brief and the critic, combined
          with each point's own guidance. Not shown to players. */}
      <div className="quest-info-section">
        <h4>Pipeline Guidance 🧭</h4>
        <EditableField
          field="author_guidance"
          value={currentQuestInfo.author_guidance || ''}
          multiline={true}
          maxLength={4096}
        />
      </div>
    </div>
  );
};

export default QuestInfoEditor;
